import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { Typography } from '@components/Typography';
import { InternalLink } from '@components/Link';
import { PhonePreviewFrame } from '@components/PhonePreviewFrame';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { accountService } from '@services';
import { useLocations, useTranslation } from '@hooks';
import { getCompanyAddress } from '@helpers/companies';
import { RoutePath } from '@constants/routes';
import { InfoOutlinedIcon } from 'assets/images/icons';

const PreviewMicrosite = ({ slug }) => {
    const [isLoaded, setIsLoaded] = useState(false);

    const [previewSocialLinks, setPreviewSocialLinks] = useState({});
    const { count, averageRating } = useSelector(state => state.microsites);
    const brands = useSelector(state => state.brands.data);

    const { watch } = useFormContext();
    const { t } = useTranslation();
    const { locationsMap } = useLocations();
    const iframeRef = useRef(null);

    const locationType = watch('locationType');
    const locationId = watch('locationId');
    const brandId = watch('brandId');
    const links = watch('links');
    const socialMediaEnable = watch('socialMediaEnable');
    const logoUrl = watch('logoUrl');
    const showReviews = watch('showReviews');

    const isBrand = locationType === LOCATION_TYPE.BRAND;

    useEffect(() => {
        const previewLocationId = isBrand ? brands?.find(b => b.id === brandId)?.locations?.[0]?.id : locationId;
        if (!previewLocationId) {
            setPreviewSocialLinks({});
            return undefined;
        }
        let active = true;
        accountService
            .getLocation(previewLocationId)
            .then(detail => {
                if (active) setPreviewSocialLinks(detail?.socialLinks || {});
            })
            .catch(() => {
                if (active) setPreviewSocialLinks({});
            });
        return () => {
            active = false;
        };
    }, [locationId, brandId, isBrand, brands]);

    useEffect(() => {
        const controller = new AbortController();
        const setPreviewData = event => {
            if (event.origin === import.meta.env.REACT_APP_SURVEY_DOMAIN) {
                const message = event.data;
                if (message.type === 'pl-preview-iframe-loaded') {
                    setIsLoaded(true);
                }
            }
        };
        window.addEventListener('message', setPreviewData, { signal: controller.signal });
        return () => controller.abort();
    }, []);

    useEffect(() => {
        const selectedLocation = locationsMap[locationId] || {};

        const locationName = isBrand ? brands?.find(b => b.id === brandId)?.title || '' : selectedLocation.locationName;

        iframeRef.current?.contentWindow?.postMessage(
            {
                type: 'pl-preview-microsite',
                data: {
                    logoUrl,
                    locationName,
                    businessAddress: isBrand
                        ? ''
                        : getCompanyAddress(selectedLocation.addressLine1, selectedLocation.city),
                    hasBusinessAddress: isBrand ? false : selectedLocation.hasBusinessAddress,
                    showReviews,
                    links,
                    socialMediaEnable,
                    socialMediaLinks: {
                        facebook: previewSocialLinks?.facebook,
                        instagram: previewSocialLinks?.instagram,
                        youtube: previewSocialLinks?.youTube,
                        x: previewSocialLinks?.x,
                        linkedIn: previewSocialLinks?.linkedIn,
                        tiktok: previewSocialLinks?.tikTok,
                    },
                    reviews: {
                        count,
                        rating: averageRating,
                    },
                },
            },
            '*',
        );
    }, [
        isLoaded,
        locationType,
        locationId,
        brandId,
        brands,
        previewSocialLinks,
        links,
        socialMediaEnable,
        logoUrl,
        showReviews,
        count,
        averageRating,
    ]);

    return (
        <>
            <Typography
                variant="caption"
                sx={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '16px', textAlign: 'center' }}
            >
                <InfoOutlinedIcon style={{ transform: 'translateY(4px)', marginRight: '8px' }} />
                <Trans t={t} i18nKey={'FeedbackSurveys.changeAppearance'}>
                    <InternalLink
                        to={RoutePath.AccountSettingsBrands}
                        sx={{ color: 'var(--primary-main)', cursor: 'pointer', textDecoration: 'none' }}
                    >
                        {{ brandSettings: t('FeedbackSurveys.brandSettings') }}
                    </InternalLink>
                </Trans>
            </Typography>
            <PhonePreviewFrame>
                <iframe
                    ref={iframeRef}
                    allow="autoplay"
                    width="100%"
                    height="100%"
                    src={`${import.meta.env.REACT_APP_SURVEY_DOMAIN}/preview/${slug}`}
                    frameBorder="0"
                ></iframe>
            </PhonePreviewFrame>
        </>
    );
};

export default PreviewMicrosite;
